// Visual align P1-5 -- shared interactive focus-chip strip for View A pages.
// Selecting a chip emphasizes the relevant table columns/rows and dims the rest.
// (The Design's focusMap wasn't fully extracted in recon; this is a reasonable
// mapping based on the focus names -- see dimStyle/rowDim below.)
import React from 'react';
import { lineText } from '../shell/ActionIcons';

export const FOCUSES = [
  { id: 'monitor', label: 'Monitor', hint: 'Overview — all signals shown.' },
  { id: 'operations', label: 'Operations', hint: 'Emphasizing daily operations (birds, occupancy, feed, mortality).' },
  { id: 'problems', label: 'Problems', hint: 'Emphasizing rows with active alerts.' },
  { id: 'health', label: 'Health', hint: 'Emphasizing flock-health signals (HD%, mortality, bird count).' },
  { id: 'finance', label: 'Finance', hint: 'Emphasizing financial performance (profit, revenue, cost).' },
  { id: 'todo', label: 'To-do', hint: 'Emphasizing rows that need action.' },
];

// Each focus emphasizes one semantic tag. Monitor emphasizes nothing -> no dimming.
const FOCUS_TAG = { operations: 'ops', problems: 'alerts', health: 'health', finance: 'finance', todo: 'alerts' };

export const DIMMED = { opacity: 0.42, filter: 'saturate(.55)' };

// Region-level focus map, lifted from the Standalone emphasis() (l.2908-2916), trimmed
// to the region keys that exist on the View A pages: chart, table, kpiBar, donuts,
// next7days, the work-queue blocks (alerts/upcoming/tasks), opsWidget (View B) and the
// individual KPI cards (kpi-finance/eggs/feed/mortality/birds/hd/alerts).
const FOCUS_MAP = {
  monitor:    { hi: [], dim: [] },
  finance:    { hi: ['chart', 'table', 'kpiBar', 'kpi-finance', 'kpi-alerts', 'donuts'], dim: ['alerts', 'upcoming', 'tasks', 'opsWidget', 'next7days', 'kpi-mortality', 'kpi-eggs', 'kpi-feed', 'kpi-birds', 'kpi-hd'] },
  problems:   { hi: ['alerts', 'kpi-alerts', 'kpi-mortality'], dim: ['upcoming', 'tasks', 'chart', 'table', 'opsWidget', 'next7days', 'donuts', 'kpi-finance', 'kpi-eggs', 'kpi-feed', 'kpi-birds', 'kpi-hd'] },
  operations: { hi: ['chart', 'table', 'opsWidget', 'kpiBar', 'kpi-mortality', 'kpi-eggs', 'kpi-feed', 'kpi-birds', 'kpi-hd', 'next7days'], dim: ['alerts', 'donuts', 'kpi-finance', 'tasks', 'upcoming'] },
  health:     { hi: ['kpi-mortality', 'kpi-alerts', 'kpi-hd', 'chart', 'kpiBar', 'alerts', 'upcoming', 'tasks', 'table'], dim: ['opsWidget', 'next7days', 'donuts', 'kpi-birds', 'kpi-eggs', 'kpi-feed', 'kpi-finance'] },
  todo:       { hi: ['tasks', 'opsWidget', 'upcoming', 'next7days'], dim: ['chart', 'alerts', 'table', 'kpiBar', 'kpi-finance', 'kpi-mortality', 'kpi-eggs', 'kpi-feed', 'kpi-birds', 'kpi-alerts', 'kpi-hd', 'donuts'] },
};

// Region-level focus style (port of the Standalone blkStyle). `key` = region name;
// kind 'block' uses the inset-highlight variant (work-queue blocks). Highlighted ->
// ring/soft bg; dimmed -> faded; neutral -> null.
export function focusStyle(focus, key, kind) {
  const m = FOCUS_MAP[focus] || FOCUS_MAP.monitor;
  const t = { transition: 'opacity .28s ease, box-shadow .28s ease, background .28s ease' };
  if (m.hi.includes(key)) {
    return kind === 'block'
      ? { ...t, background: '#eef5f0', borderRadius: 8, padding: '8px 10px', margin: '0 -4px', boxShadow: 'inset 0 0 0 1px rgba(64,145,79,.32)' }
      : { ...t, boxShadow: '0 0 0 1.5px rgba(64,145,79,.55), 0 6px 18px rgba(27,67,50,.10)' };
  }
  if (m.dim.includes(key)) return { ...t, ...DIMMED };
  return t;
}

// Style to spread onto a cell. `tags` = the semantic tags this column belongs to.
// Identity columns tagged 'keep' are never dimmed. Returns null when nothing changes.
export function dimStyle(focus, tags = []) {
  if (focus === 'monitor' || tags.includes('keep')) return null;
  return tags.includes(FOCUS_TAG[focus]) ? null : DIMMED;
}

// For Problems / To-do: dim rows that have no active alert.
export function rowDim(focus, hasAlert) {
  return (focus === 'problems' || focus === 'todo') && !hasAlert ? DIMMED : null;
}

export default function FocusChips({ focus = 'monitor', onChange }) {
  const active = FOCUSES.find(f => f.id === focus) || FOCUSES[0];
  return (
    <div style={wrap}>
      <div style={chipRow}>
        {FOCUSES.map(f => (
          <button key={f.id} onClick={() => onChange && onChange(f.id)}
            style={{ ...chip, ...(f.id === focus ? chipOn : chipOff) }}>{lineText(f.label)}</button>
        ))}
      </div>
      <div style={hint}>{active.hint}</div>
    </div>
  );
}

const wrap = { marginBottom: 12 };
const chipRow = { display: 'flex', gap: 8, flexWrap: 'wrap' };
const chip = { padding: '5px 12px', borderRadius: 7, cursor: 'pointer', fontSize: 12, fontWeight: 600, transition: 'all .15s' };
const chipOn = { background: '#1b4332', color: '#fff', border: '1px solid #1b4332' };
const chipOff = { background: '#fff', color: '#5a6a5f', border: '1px solid #e0e5e0' };
const hint = { fontSize: 11, color: '#8a948e', marginTop: 7 };
